import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, BadgeCheck } from "lucide-react";
import { SERVICES, CITIES } from "../data/content";
import { PRICING, CORE_CITIES, OUTER_TRAVEL_FEE } from "../config/pricing";

const OTHER = "__other";
const inputCls =
  "w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-600 focus:ring-2 focus:ring-teal-500/40";

export function QuoteEstimator() {
  const [service, setService] = useState("");
  const [headcount, setHeadcount] = useState(5);
  const [city, setCity] = useState("");

  const estimate = useMemo(() => {
    const rate = PRICING.services[service];
    if (!rate) return null;
    const isCore = CORE_CITIES.includes(city);
    const isKnown = CITIES.includes(city);
    const travel = city ? (isCore ? 0 : OUTER_TRAVEL_FEE) : 0;
    const total = rate.base + rate.perPerson * headcount + travel;
    const round5 = (n) => Math.max(5, Math.round(n / 5) * 5);
    return {
      low: round5(total * 0.9),
      high: round5(total * 1.1),
      base: rate.base,
      perPerson: rate.perPerson,
      travel,
      travelNote: !city ? null : isCore ? "No travel fee — core corridor" : isKnown ? "Travel within service area" : "Travel to be confirmed",
    };
  }, [service, headcount, city]);

  const requestUrl = `/contact?service=${encodeURIComponent(service)}&city=${encodeURIComponent(
    city === OTHER ? "" : city
  )}&headcount=${headcount}`;

  return (
    <div
      data-testid="quote-estimator"
      className="grid overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm lg:grid-cols-5"
    >
      {/* INPUTS */}
      <div className="p-6 sm:p-8 lg:col-span-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
            <Calculator className="h-5 w-5" />
          </span>
          <h3 className="font-display text-xl font-bold text-slate-900 sm:text-2xl">
            Instant ballpark estimate
          </h3>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">
          Three quick picks and you'll see a working price range — no email required.
        </p>

        <div className="mt-6 space-y-5">
          <div>
            <label htmlFor="quote-service" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              Service
            </label>
            <select
              id="quote-service"
              data-testid="quote-service-select"
              value={service}
              onChange={(e) => setService(e.target.value)}
              className={inputCls}
            >
              <option value="" disabled>Choose a service…</option>
              {SERVICES.map((s) => (
                <option key={s.id} value={s.title}>{s.title}</option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label htmlFor="quote-headcount" className="text-xs font-bold uppercase tracking-wider text-slate-500">
                People to test
              </label>
              <span
                data-testid="quote-headcount-value"
                className="rounded-full bg-teal-700 px-3 py-1 font-display text-sm font-bold text-white"
              >
                {headcount}
              </span>
            </div>
            <input
              id="quote-headcount"
              data-testid="quote-headcount-slider"
              type="range"
              min="1"
              max="50"
              value={headcount}
              onChange={(e) => setHeadcount(Number(e.target.value))}
              className="h-2 w-full cursor-pointer appearance-none rounded-full bg-slate-200 accent-teal-700"
            />
            <div className="mt-1 flex justify-between text-[11px] font-semibold text-slate-400">
              <span>1</span>
              <span>50+</span>
            </div>
          </div>

          <div>
            <label htmlFor="quote-city" className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">
              City
            </label>
            <select
              id="quote-city"
              data-testid="quote-city-select"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className={inputCls}
            >
              <option value="" disabled>Where would we come to?</option>
              {CITIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
              <option value={OTHER}>Somewhere else in Eastern Iowa</option>
            </select>
          </div>
        </div>
      </div>

      {/* RESULT */}
      <div className="flex flex-col justify-between bg-slate-900 p-6 sm:p-8 lg:col-span-2">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-teal-400">
            Your estimate
          </span>
          {estimate ? (
            <>
              <p
                data-testid="quote-result-range"
                className="mt-3 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl"
              >
                ${estimate.low}–${estimate.high}
              </p>
              <ul data-testid="quote-result" className="mt-5 space-y-2 text-sm text-slate-300">
                <li className="flex justify-between gap-4">
                  <span>On-site visit</span>
                  <span className="font-semibold text-white">${estimate.base}</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>
                    {headcount} {headcount === 1 ? "person" : "people"} × ${estimate.perPerson}
                  </span>
                  <span className="font-semibold text-white">${estimate.perPerson * headcount}</span>
                </li>
                <li className="flex justify-between gap-4">
                  <span>Travel</span>
                  <span className="font-semibold text-white">
                    {estimate.travel === 0 ? "Included" : `$${estimate.travel}`}
                  </span>
                </li>
              </ul>
              {estimate.travelNote && (
                <p className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-teal-300">
                  <BadgeCheck className="h-4 w-4" /> {estimate.travelNote}
                </p>
              )}
            </>
          ) : (
            <p data-testid="quote-result-empty" className="mt-4 text-sm leading-relaxed text-slate-400">
              Pick a service on the left and your estimate appears here instantly — adjust headcount
              and city to see it update live.
            </p>
          )}
        </div>
        <div className="mt-8">
          {estimate && (
            <>
              <Link
                data-testid="quote-request-link"
                to={requestUrl}
                className="flex w-full items-center justify-center gap-2 rounded-full bg-teal-500 px-6 py-3.5 text-sm font-bold text-slate-900 transition-all hover:-translate-y-0.5 hover:bg-teal-400"
              >
                Request this testing <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                data-testid="quote-paypal-deposit-link"
                to={`/payments?service=${encodeURIComponent(service)}&headcount=${headcount}&city=${encodeURIComponent(city === OTHER ? "" : city)}&deposit=1`}
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-white/25 px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-white/10"
              >
                Reserve now — pay deposit online
              </Link>
            </>
          )}
          <p className="mt-3 text-center text-[11px] leading-relaxed text-slate-500">
            Ballpark only — final quote confirmed by our team before anything is scheduled.
          </p>
        </div>
      </div>
    </div>
  );
}
